// ---------------------------------------------------------------------------
// studio-new-project-copy.ts — every readable string on `/studio/new`.
//
// WHY THIS IS A MODULE AND NOT FIVE BLOCKS IN `studio-dictionaries.ts`:
// the same reason `axes-copy.ts` exists. The dictionary ROUTES the active
// locale's table; the table itself lives next to nothing else so a wizard
// string is edited in one file rather than in five places 1300 lines apart.
//
// WHY THE WHOLE WIZARD AND NOT ONLY STEP 1: the register that measures this
// screen drives the route and reads the first paint, so only step 1, the
// stepper and the footer are ever harvested. Translating just those would
// have left a Korean step 1 handing over to an English step 2 — the same
// half-localized screen #2324 produced on the accessibility surface, one
// interaction later. All four steps are here.
//
// KEEP-LATIN, deliberate: `cogo` (brand), `Flutter` and `React Native`
// (product names — a framework is called Flutter in every language), and
// `Instagram` in the example prompt (proper noun). Everything else that a
// reader can see is translated.
// ---------------------------------------------------------------------------

/** One of the four starting points step 1 offers. */
export interface StarterPathCopy {
  title: string;
  badge: string;
  description: string;
  /** Seeds the composer when the user picks a path without typing — it
   *  becomes the user's own words, so it is translated like visible copy. */
  examplePrompt: string;
  /** Become the project's name and description downstream; both are read
   *  back on steps 3 and 4 as visible text. */
  fallbackName: string;
  fallbackDescription: string;
}

interface LabelAndDesc {
  label: string;
  desc: string;
}

export interface NewProjectCopy {
  /** `aria-label` on the wizard's main region. */
  mainArea: string;
  sidebarTitle: string;
  /** `aria-label` on the step list. */
  stepsNav: string;
  unsavedNote: string;
  steps: {
    start: { label: string; sublabel: string };
    style: { label: string; sublabel: string };
    shape: { label: string; sublabel: string };
    create: { label: string; sublabel: string };
  };
  footer: {
    back: string;
    cancel: string;
    next: string;
    createProject: string;
    createWithCogo: string;
    creating: string;
  };
  step1: {
    kicker: string;
    lead: string;
    promptLabel: string;
    promptOptional: string;
    promptPlaceholder: string;
    hint: string;
    startsFromStarter: string;
    startsFromDescription: string;
    choose: string;
    paths: {
      creatorPage: StarterPathCopy;
      clientApp: StarterPathCopy;
      launchSell: StarterPathCopy;
      teamWorkflow: StarterPathCopy;
    };
  };
  step2Template: {
    heading: string;
    lead: string;
    searchPlaceholder: string;
    searchLabel: string;
    all: string;
    loading: string;
    emptyFiltered: (searchText: string) => string;
    emptyNone: string;
    preview: string;
    manifestFailed: string;
    invalidJsonResponse: string;
    invalidManifestFormat: string;
    loadFailed: string;
    /** The brainstorm escape hatch beside the search field. */
    exploreMore: string;
  };
  step2Scratch: {
    heading: string;
    lead: string;
    approvedSystems: string;
    loadingReleases: string;
    noProjectRelease: (runtimeList: string) => string;
    immutableRelease: string;
    personalStyles: string;
    loading: string;
    noPersonalStyle: string;
    resaveLegacy: string;
    createNewStyle: string;
    nameLabel: string;
    defaultStyleName: string;
    enterStyleName: string;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    enterStyleDescription: string;
    saveFailed: string;
    unknownError: string;
    cancel: string;
    saving: string;
    saveAndContinue: string;
    /**
     * The six axis names as THIS screen words them. Deliberately not
     * `designSystemAxes.axisLabels`: that table says `Base color`,
     * `Chart colors` and `Corner radius` where this screen says `Base Color`,
     * `Chart` and `Radius`, and routing through it would have changed the
     * English surface — which this lane is required to leave byte-for-byte.
     */
    axis: {
      style: string;
      baseColor: string;
      theme: string;
      chart: string;
      radius: string;
      font: string;
    };
  };
  step3: {
    heading: string;
    lead: string;
    nameLabel: string;
    namePlaceholder: string;
    nameRequiredHint: string;
    descriptionLabel: string;
    descriptionOptional: string;
    descriptionPlaceholder: string;
    workType: string;
    whereItRuns: string;
    reviewLevel: string;
    summaryStartingPoint: string;
    summaryStarter: string;
    summaryStyle: string;
    summaryWorkShape: string;
    fromStarterSuffix: (labelName: string) => string;
    fromScratch: string;
    starterFallback: string;
    appClass: { static: LabelAndDesc; dynamic: LabelAndDesc; hybrid: LabelAndDesc };
    runtime: {
      web: LabelAndDesc;
      mobile: LabelAndDesc;
      flutter: LabelAndDesc;
      rn: LabelAndDesc;
      desktop: LabelAndDesc;
    };
    security: {
      standard: LabelAndDesc;
      enterprise: LabelAndDesc;
      financial: LabelAndDesc;
    };
  };
  step4: {
    heading: string;
    leadGenerate: string;
    leadProject: string;
    summaryName: string;
    summaryWhatItShouldDo: string;
    summaryRunsOn: string;
    successGenerate: string;
    successProject: string;
    openingWorkspace: string;
    openingEditor: string;
    generationNoteTitle: string;
    generationNoteBody: string;
    retry: string;
    personalStyleSuffix: (labelName: string) => string;
    approvedReleaseSuffix: (labelName: string, releaseVersion: string) => string;
  };
  errors: {
    untitled: string;
    createFailed: string;
    noProjectId: string;
    unexpectedError: string;
    clarificationRequired: string;
    notReady: string;
    preparingRequest: string;
    creatingWorkspace: string;
  };
}

export const EN_NEW_PROJECT_COPY: NewProjectCopy = {
  mainArea: "Create a new project",
  sidebarTitle: "New work",
  stepsNav: "Wizard steps",
  unsavedNote: "Changes are not saved until you create the project.",
  steps: {
    start: { label: "Start", sublabel: "Describe the goal" },
    style: { label: "Style", sublabel: "Starter or look" },
    shape: { label: "Shape", sublabel: "Name and choices" },
    create: { label: "Create", sublabel: "Open your workspace" },
  },
  footer: {
    back: "← Back",
    cancel: "Cancel",
    next: "Next →",
    createProject: "Create project",
    createWithCogo: "Create with cogo",
    creating: "Creating...",
  },
  step1: {
    kicker: "Start from the work",
    lead: "Describe the application, document, or report you want. Then choose the starting point that feels closest.",
    promptLabel: "App description",
    promptOptional: "optional, but recommended",
    promptPlaceholder:
      "Example: Build a booking page for a beauty creator with services, testimonials, Instagram links, and a payment-ready call to action.",
    hint: "You can still choose a Starter or visual style on the next step before creating the project.",
    startsFromStarter: "Starts from a Starter",
    startsFromDescription: "Starts from your description",
    choose: "Choose",
    paths: {
      creatorPage: {
        title: "Creator page",
        badge: "Beginner",
        description:
          "Personal brand, link-in-bio, event, course, fan page, or product teaser.",
        examplePrompt:
          "Make a personal brand page with profile, featured links, latest posts, and a contact form.",
        fallbackName: "Creator Launch Page",
        fallbackDescription:
          "A public creator page with profile, links, content highlights, and a contact path.",
      },
      clientApp: {
        title: "Client app",
        badge: "For teams",
        description:
          "A client portal, dashboard, intake, or handoff app for real work.",
        examplePrompt:
          "Create a client portal with dashboard cards, request intake, project status, and file handoff.",
        fallbackName: "Client Portal",
        fallbackDescription:
          "A client-facing app with status, intake, and handoff workflows.",
      },
      launchSell: {
        title: "Launch and sell",
        badge: "Monetization",
        description:
          "A page or app designed to publish, collect leads, sell, book, or validate revenue.",
        examplePrompt:
          "Build a launch page for a paid workshop with offer sections, checkout CTA, FAQ, and email capture.",
        fallbackName: "Revenue Launch App",
        fallbackDescription:
          "A monetization-focused launch app with offer, CTA, FAQ, and capture flow.",
      },
      teamWorkflow: {
        title: "Team workflow",
        badge: "Approvals",
        description:
          "A guided workspace for requests, review, approvals, and handoffs.",
        examplePrompt:
          "Prepare an operations workspace with request intake, review steps, owner notes, and approval status.",
        fallbackName: "Team Workflow App",
        fallbackDescription:
          "A team workflow app with request intake, review steps, owners, and approvals.",
      },
    },
  },
  step2Template: {
    heading: "Choose a Starter",
    lead: "Select a cogo-ready starting point for the artifact you want to create.",
    searchPlaceholder: "Search Starters...",
    searchLabel: "Search Starters",
    all: "All",
    loading: "Loading Starters…",
    emptyFiltered: (searchText) => `No Starters match "${searchText}"`,
    emptyNone: "No Starters available.",
    preview: "Preview",
    manifestFailed: "Failed to load template manifest",
    invalidJsonResponse: "invalid JSON response",
    invalidManifestFormat: "invalid manifest format",
    loadFailed: "Load failed",
    exploreMore: "Explore more",
  },
  step2Scratch: {
    heading: "Look and feel",
    lead: "Approved systems are immutable and enforced through generation and publishing. Personal styles control visual tokens only.",
    approvedSystems: "Approved systems",
    loadingReleases: "Loading approved releases…",
    noProjectRelease: (runtimeList) =>
      `No approved project-wide system is available for ${runtimeList} yet. Component-only releases are kept out of this selector.`,
    immutableRelease: "immutable release",
    personalStyles: "Personal styles",
    loading: "Loading…",
    noPersonalStyle:
      "You don't have a personal style yet. Create one below. Personal styles are editable and are not ontology releases.",
    resaveLegacy: "Re-save this legacy style before using it for generation.",
    createNewStyle: "Create a new style",
    nameLabel: "Name",
    defaultStyleName: "My Style",
    enterStyleName: "Please enter a style name.",
    descriptionLabel: "Description",
    descriptionPlaceholder: "Describe the style you want to create.",
    enterStyleDescription: "Please enter a style description.",
    saveFailed: "Save failed",
    unknownError: "Unknown error",
    cancel: "Cancel",
    saving: "Saving…",
    saveAndContinue: "Save and continue",
    axis: {
      style: "Style",
      baseColor: "Base Color",
      theme: "Theme",
      chart: "Chart",
      radius: "Radius",
      font: "Font",
    },
  },
  step3: {
    heading: "Shape the work",
    lead: "Check the name, where it should run, and how much review it needs before cogo opens the workspace.",
    nameLabel: "Name",
    namePlaceholder: "e.g. Client commerce review",
    nameRequiredHint: "Required — at least one character.",
    descriptionLabel: "What it should do",
    descriptionOptional: "(optional)",
    descriptionPlaceholder: "What should this app do?",
    workType: "Work type",
    whereItRuns: "Where it should run",
    reviewLevel: "Review level",
    summaryStartingPoint: "Starting point",
    summaryStarter: "Starter",
    summaryStyle: "Style",
    summaryWorkShape: "Work shape",
    fromStarterSuffix: (labelName) => `${labelName} (from Starter)`,
    fromScratch: "From scratch",
    starterFallback: "Starter",
    appClass: {
      static: { label: "Simple page", desc: "Content and layout" },
      dynamic: { label: "Interactive app", desc: "Data and actions" },
      hybrid: { label: "Page with tools", desc: "Content plus interaction" },
    },
    runtime: {
      web: { label: "Web", desc: "Browser" },
      mobile: { label: "Mobile", desc: "Phone and tablet" },
      flutter: { label: "Flutter", desc: "Flutter app" },
      rn: { label: "React Native", desc: "Native app" },
      desktop: { label: "Desktop", desc: "Desktop app" },
    },
    security: {
      standard: { label: "Standard", desc: "Everyday work" },
      enterprise: { label: "Team review", desc: "Policy review" },
      financial: { label: "Sensitive", desc: "Money or private data" },
    },
  },
  step4: {
    heading: "Ready to create",
    leadGenerate: "Confirm the brief. cogo will create the work and open the editor.",
    leadProject: "Confirm your choices and create your project.",
    summaryName: "Name",
    summaryWhatItShouldDo: "What it should do",
    summaryRunsOn: "Runs on",
    successGenerate: "Creation started!",
    successProject: "Project created!",
    openingWorkspace: "Opening your workspace...",
    openingEditor: "Opening editor...",
    generationNoteTitle: "Create with cogo",
    generationNoteBody:
      "cogo will create the workspace from your description. You can review, refine, and launch it from the editor.",
    retry: "Retry",
    personalStyleSuffix: (labelName) => `${labelName} (personal style)`,
    approvedReleaseSuffix: (labelName, releaseVersion) =>
      `${labelName} · ${releaseVersion} (approved release)`,
  },
  errors: {
    untitled: "Untitled",
    createFailed: "Create failed",
    noProjectId: "No project ID returned",
    unexpectedError: "Unexpected error",
    clarificationRequired: "Source assessment is required before generation",
    notReady: "This project is not ready to generate yet",
    preparingRequest: "Preparing your request",
    creatingWorkspace: "Creating workspace",
  },
};

export const KO_NEW_PROJECT_COPY: NewProjectCopy = {
  mainArea: "새 프로젝트 만들기",
  sidebarTitle: "새 작업",
  stepsNav: "마법사 단계",
  unsavedNote: "프로젝트를 만들기 전까지는 변경 사항이 저장되지 않습니다.",
  steps: {
    start: { label: "시작", sublabel: "목표 설명" },
    style: { label: "스타일", sublabel: "스타터 또는 외관" },
    shape: { label: "구성", sublabel: "이름과 선택" },
    create: { label: "생성", sublabel: "작업 공간 열기" },
  },
  footer: {
    back: "← 뒤로",
    cancel: "취소",
    next: "다음 →",
    createProject: "프로젝트 만들기",
    createWithCogo: "cogo로 만들기",
    creating: "만드는 중...",
  },
  step1: {
    kicker: "작업에서 시작하기",
    lead: "만들고 싶은 애플리케이션, 문서, 리포트를 설명해 주세요. 그런 다음 가장 가까운 시작점을 고르면 됩니다.",
    promptLabel: "앱 설명",
    promptOptional: "선택 사항이지만 권장합니다",
    promptPlaceholder:
      "예: 서비스 소개, 이용 후기, 인스타그램 링크, 결제로 이어지는 행동 유도를 갖춘 뷰티 크리에이터용 예약 페이지를 만들어 주세요.",
    hint: "프로젝트를 만들기 전, 다음 단계에서 스타터나 시각 스타일을 고를 수 있습니다.",
    startsFromStarter: "스타터에서 시작합니다",
    startsFromDescription: "설명에서 시작합니다",
    choose: "고르기",
    paths: {
      creatorPage: {
        title: "크리에이터 페이지",
        badge: "입문",
        description:
          "개인 브랜드, 링크 모음, 행사, 강의, 팬 페이지, 제품 티저.",
        examplePrompt:
          "프로필, 대표 링크, 최신 글, 문의 양식을 갖춘 개인 브랜드 페이지를 만들어 주세요.",
        fallbackName: "크리에이터 공개 페이지",
        fallbackDescription:
          "프로필, 링크, 콘텐츠 하이라이트, 문의 경로를 갖춘 공개 크리에이터 페이지.",
      },
      clientApp: {
        title: "클라이언트 앱",
        badge: "팀용",
        description:
          "실무를 위한 클라이언트 포털, 대시보드, 접수, 인수인계 앱.",
        examplePrompt:
          "대시보드 카드, 요청 접수, 프로젝트 상태, 파일 인수인계를 갖춘 클라이언트 포털을 만들어 주세요.",
        fallbackName: "클라이언트 포털",
        fallbackDescription:
          "상태, 접수, 인수인계 흐름을 갖춘 클라이언트용 앱.",
      },
      launchSell: {
        title: "출시하고 판매하기",
        badge: "수익화",
        description:
          "공개, 잠재 고객 수집, 판매, 예약, 매출 검증을 위해 설계된 페이지 또는 앱.",
        examplePrompt:
          "제안 구획, 결제 유도, 자주 묻는 질문, 이메일 수집을 갖춘 유료 워크숍 출시 페이지를 만들어 주세요.",
        fallbackName: "매출 출시 앱",
        fallbackDescription:
          "제안, 행동 유도, 자주 묻는 질문, 수집 흐름을 갖춘 수익화 중심 출시 앱.",
      },
      teamWorkflow: {
        title: "팀 업무 흐름",
        badge: "승인",
        description: "요청, 검토, 승인, 인수인계를 안내하는 작업 공간.",
        examplePrompt:
          "요청 접수, 검토 단계, 담당자 메모, 승인 상태를 갖춘 운영 작업 공간을 준비해 주세요.",
        fallbackName: "팀 업무 흐름 앱",
        fallbackDescription:
          "요청 접수, 검토 단계, 담당자, 승인을 갖춘 팀 업무 흐름 앱.",
      },
    },
  },
  step2Template: {
    heading: "스타터 고르기",
    lead: "만들려는 산출물에 맞는, cogo가 바로 쓸 수 있는 시작점을 고르세요.",
    searchPlaceholder: "스타터 검색...",
    searchLabel: "스타터 검색",
    all: "전체",
    loading: "스타터 불러오는 중…",
    emptyFiltered: (searchText) => `"${searchText}"과(와) 일치하는 스타터가 없습니다`,
    emptyNone: "사용할 수 있는 스타터가 없습니다.",
    preview: "미리보기",
    manifestFailed: "템플릿 목록을 불러오지 못했습니다",
    invalidJsonResponse: "올바르지 않은 JSON 응답",
    invalidManifestFormat: "올바르지 않은 매니페스트 형식",
    loadFailed: "불러오지 못했습니다",
    exploreMore: "더 살펴보기",
  },
  step2Scratch: {
    heading: "외관과 느낌",
    lead: "승인된 시스템은 바꿀 수 없으며 생성과 게시 전 과정에서 강제됩니다. 개인 스타일은 시각 토큰만 제어합니다.",
    approvedSystems: "승인된 시스템",
    loadingReleases: "승인된 릴리스 불러오는 중…",
    noProjectRelease: (runtimeList) =>
      `${runtimeList}에 쓸 수 있는 승인된 프로젝트 전역 시스템이 아직 없습니다. 컴포넌트 전용 릴리스는 이 선택기에서 제외됩니다.`,
    immutableRelease: "변경 불가 릴리스",
    personalStyles: "개인 스타일",
    loading: "불러오는 중…",
    noPersonalStyle:
      "아직 개인 스타일이 없습니다. 아래에서 하나 만들어 보세요. 개인 스타일은 편집할 수 있으며 온톨로지 릴리스가 아닙니다.",
    resaveLegacy: "생성에 쓰려면 이 예전 스타일을 다시 저장하세요.",
    createNewStyle: "새 스타일 만들기",
    nameLabel: "이름",
    defaultStyleName: "내 스타일",
    enterStyleName: "스타일 이름을 입력하세요.",
    descriptionLabel: "설명",
    descriptionPlaceholder: "만들려는 스타일을 설명하세요.",
    enterStyleDescription: "스타일 설명을 입력하세요.",
    saveFailed: "저장하지 못했습니다",
    unknownError: "알 수 없는 오류",
    cancel: "취소",
    saving: "저장 중…",
    saveAndContinue: "저장하고 계속",
    axis: {
      style: "스타일",
      baseColor: "기본 색",
      theme: "테마",
      chart: "차트",
      radius: "모서리 반경",
      font: "글꼴",
    },
  },
  step3: {
    heading: "작업 구성하기",
    lead: "cogo가 작업 공간을 열기 전에 이름, 실행 위치, 필요한 검토 수준을 확인하세요.",
    nameLabel: "이름",
    namePlaceholder: "예: 클라이언트 커머스 검토",
    nameRequiredHint: "필수 — 한 글자 이상 입력하세요.",
    descriptionLabel: "무엇을 하는 작업인가요",
    descriptionOptional: "(선택)",
    descriptionPlaceholder: "이 앱은 무엇을 해야 하나요?",
    workType: "작업 유형",
    whereItRuns: "실행 위치",
    reviewLevel: "검토 수준",
    summaryStartingPoint: "시작점",
    summaryStarter: "스타터",
    summaryStyle: "스타일",
    summaryWorkShape: "작업 형태",
    fromStarterSuffix: (labelName) => `${labelName} (스타터 기반)`,
    fromScratch: "빈 상태에서",
    starterFallback: "스타터",
    appClass: {
      static: { label: "간단한 페이지", desc: "콘텐츠와 배치" },
      dynamic: { label: "동작하는 앱", desc: "데이터와 동작" },
      hybrid: { label: "도구가 있는 페이지", desc: "콘텐츠와 상호작용" },
    },
    runtime: {
      web: { label: "웹", desc: "브라우저" },
      mobile: { label: "모바일", desc: "휴대폰과 태블릿" },
      flutter: { label: "Flutter", desc: "Flutter 앱" },
      rn: { label: "React Native", desc: "네이티브 앱" },
      desktop: { label: "데스크톱", desc: "데스크톱 앱" },
    },
    security: {
      standard: { label: "표준", desc: "일상 업무" },
      enterprise: { label: "팀 검토", desc: "정책 검토" },
      financial: { label: "민감", desc: "금전 또는 개인 정보" },
    },
  },
  step4: {
    heading: "만들 준비가 되었습니다",
    leadGenerate: "요청 내용을 확인하세요. cogo가 작업을 만들고 편집기를 엽니다.",
    leadProject: "고른 내용을 확인하고 프로젝트를 만드세요.",
    summaryName: "이름",
    summaryWhatItShouldDo: "무엇을 하는 작업인가요",
    summaryRunsOn: "실행 대상",
    successGenerate: "생성을 시작했습니다!",
    successProject: "프로젝트를 만들었습니다!",
    openingWorkspace: "작업 공간을 여는 중...",
    openingEditor: "편집기를 여는 중...",
    generationNoteTitle: "cogo로 만들기",
    generationNoteBody:
      "cogo가 설명을 바탕으로 작업 공간을 만듭니다. 편집기에서 검토하고 다듬은 뒤 내보낼 수 있습니다.",
    retry: "다시 시도",
    personalStyleSuffix: (labelName) => `${labelName} (개인 스타일)`,
    approvedReleaseSuffix: (labelName, releaseVersion) =>
      `${labelName} · ${releaseVersion} (승인된 릴리스)`,
  },
  errors: {
    untitled: "제목 없음",
    createFailed: "만들지 못했습니다",
    noProjectId: "프로젝트 ID가 돌아오지 않았습니다",
    unexpectedError: "예기치 않은 오류",
    clarificationRequired: "생성하기 전에 소스 평가가 필요합니다",
    notReady: "이 프로젝트는 아직 생성할 준비가 되지 않았습니다",
    preparingRequest: "요청을 준비하는 중",
    creatingWorkspace: "작업 공간을 만드는 중",
  },
};

export const JA_NEW_PROJECT_COPY: NewProjectCopy = {
  mainArea: "新しいプロジェクトを作成",
  sidebarTitle: "新しい作業",
  stepsNav: "ウィザードのステップ",
  unsavedNote: "プロジェクトを作成するまで変更は保存されません。",
  steps: {
    start: { label: "開始", sublabel: "目標を書く" },
    style: { label: "スタイル", sublabel: "スターターまたは外観" },
    shape: { label: "構成", sublabel: "名前と選択" },
    create: { label: "作成", sublabel: "ワークスペースを開く" },
  },
  footer: {
    back: "← 戻る",
    cancel: "キャンセル",
    next: "次へ →",
    createProject: "プロジェクトを作成",
    createWithCogo: "cogo で作成",
    creating: "作成中...",
  },
  step1: {
    kicker: "作業から始める",
    lead: "作りたいアプリケーション、ドキュメント、レポートを書いてください。そのあと、いちばん近い出発点を選びます。",
    promptLabel: "アプリの説明",
    promptOptional: "任意ですが推奨します",
    promptPlaceholder:
      "例: サービス紹介、利用者の声、Instagram のリンク、決済につながる導線を備えたビューティークリエイター向け予約ページを作ってください。",
    hint: "プロジェクトを作成する前に、次のステップでスターターやビジュアルスタイルを選べます。",
    startsFromStarter: "スターターから始めます",
    startsFromDescription: "説明から始めます",
    choose: "選ぶ",
    paths: {
      creatorPage: {
        title: "クリエイターページ",
        badge: "はじめて向け",
        description:
          "パーソナルブランド、リンク集、イベント、講座、ファンページ、製品ティザー。",
        examplePrompt:
          "プロフィール、注目リンク、最新の投稿、問い合わせフォームを備えたパーソナルブランドページを作ってください。",
        fallbackName: "クリエイター公開ページ",
        fallbackDescription:
          "プロフィール、リンク、コンテンツのハイライト、問い合わせ導線を備えた公開クリエイターページ。",
      },
      clientApp: {
        title: "クライアントアプリ",
        badge: "チーム向け",
        description:
          "実務のためのクライアントポータル、ダッシュボード、受付、引き継ぎアプリ。",
        examplePrompt:
          "ダッシュボードカード、依頼受付、プロジェクト状況、ファイル引き継ぎを備えたクライアントポータルを作ってください。",
        fallbackName: "クライアントポータル",
        fallbackDescription:
          "状況、受付、引き継ぎの流れを備えたクライアント向けアプリ。",
      },
      launchSell: {
        title: "公開して売る",
        badge: "収益化",
        description:
          "公開、見込み客の獲得、販売、予約、売上の検証のために設計されたページまたはアプリ。",
        examplePrompt:
          "オファー区画、決済への導線、よくある質問、メール登録を備えた有料ワークショップの公開ページを作ってください。",
        fallbackName: "売上ローンチアプリ",
        fallbackDescription:
          "オファー、導線、よくある質問、登録の流れを備えた収益化中心のローンチアプリ。",
      },
      teamWorkflow: {
        title: "チームの業務フロー",
        badge: "承認",
        description: "依頼、レビュー、承認、引き継ぎを案内するワークスペース。",
        examplePrompt:
          "依頼受付、レビュー段階、担当者メモ、承認状況を備えた運用ワークスペースを用意してください。",
        fallbackName: "チーム業務フローアプリ",
        fallbackDescription:
          "依頼受付、レビュー段階、担当者、承認を備えたチーム業務フローアプリ。",
      },
    },
  },
  step2Template: {
    heading: "スターターを選ぶ",
    lead: "作りたい成果物に合う、cogo がすぐ使える出発点を選んでください。",
    searchPlaceholder: "スターターを検索...",
    searchLabel: "スターターを検索",
    all: "すべて",
    loading: "スターターを読み込み中…",
    emptyFiltered: (searchText) => `「${searchText}」に一致するスターターがありません`,
    emptyNone: "利用できるスターターがありません。",
    preview: "プレビュー",
    manifestFailed: "テンプレート一覧を読み込めませんでした",
    invalidJsonResponse: "無効な JSON 応答",
    invalidManifestFormat: "無効なマニフェスト形式",
    loadFailed: "読み込めませんでした",
    exploreMore: "もっと見る",
  },
  step2Scratch: {
    heading: "外観と雰囲気",
    lead: "承認済みシステムは変更できず、生成と公開の全工程で強制されます。個人スタイルは視覚トークンだけを制御します。",
    approvedSystems: "承認済みシステム",
    loadingReleases: "承認済みリリースを読み込み中…",
    noProjectRelease: (runtimeList) =>
      `${runtimeList} で使える承認済みのプロジェクト全体システムはまだありません。コンポーネント専用リリースはこの選択肢から除かれます。`,
    immutableRelease: "変更不可リリース",
    personalStyles: "個人スタイル",
    loading: "読み込み中…",
    noPersonalStyle:
      "まだ個人スタイルがありません。下から作成してください。個人スタイルは編集でき、オントロジーリリースではありません。",
    resaveLegacy: "生成に使うには、この以前のスタイルを保存し直してください。",
    createNewStyle: "新しいスタイルを作成",
    nameLabel: "名前",
    defaultStyleName: "マイスタイル",
    enterStyleName: "スタイル名を入力してください。",
    descriptionLabel: "説明",
    descriptionPlaceholder: "作成したいスタイルを説明してください。",
    enterStyleDescription: "スタイルの説明を入力してください。",
    saveFailed: "保存できませんでした",
    unknownError: "不明なエラー",
    cancel: "キャンセル",
    saving: "保存中…",
    saveAndContinue: "保存して続ける",
    axis: {
      style: "スタイル",
      baseColor: "ベースカラー",
      theme: "テーマ",
      chart: "チャート",
      radius: "角の丸み",
      font: "フォント",
    },
  },
  step3: {
    heading: "作業を組み立てる",
    lead: "cogo がワークスペースを開く前に、名前、実行先、必要なレビューの度合いを確認してください。",
    nameLabel: "名前",
    namePlaceholder: "例: クライアント向けコマースレビュー",
    nameRequiredHint: "必須 — 1文字以上入力してください。",
    descriptionLabel: "何をする作業か",
    descriptionOptional: "(任意)",
    descriptionPlaceholder: "このアプリは何をすべきですか?",
    workType: "作業の種類",
    whereItRuns: "実行先",
    reviewLevel: "レビューの度合い",
    summaryStartingPoint: "出発点",
    summaryStarter: "スターター",
    summaryStyle: "スタイル",
    summaryWorkShape: "作業の形",
    fromStarterSuffix: (labelName) => `${labelName} (スターターから)`,
    fromScratch: "ゼロから",
    starterFallback: "スターター",
    appClass: {
      static: { label: "シンプルなページ", desc: "内容とレイアウト" },
      dynamic: { label: "動くアプリ", desc: "データと操作" },
      hybrid: { label: "道具のあるページ", desc: "内容と操作の両方" },
    },
    runtime: {
      web: { label: "ウェブ", desc: "ブラウザ" },
      mobile: { label: "モバイル", desc: "スマートフォンとタブレット" },
      flutter: { label: "Flutter", desc: "Flutter アプリ" },
      rn: { label: "React Native", desc: "ネイティブアプリ" },
      desktop: { label: "デスクトップ", desc: "デスクトップアプリ" },
    },
    security: {
      standard: { label: "標準", desc: "日常の業務" },
      enterprise: { label: "チームレビュー", desc: "ポリシー確認" },
      financial: { label: "機微", desc: "金銭または個人情報" },
    },
  },
  step4: {
    heading: "作成の準備ができました",
    leadGenerate: "依頼内容を確認してください。cogo が作業を作成し、エディタを開きます。",
    leadProject: "選んだ内容を確認してプロジェクトを作成してください。",
    summaryName: "名前",
    summaryWhatItShouldDo: "何をする作業か",
    summaryRunsOn: "実行対象",
    successGenerate: "作成を開始しました!",
    successProject: "プロジェクトを作成しました!",
    openingWorkspace: "ワークスペースを開いています...",
    openingEditor: "エディタを開いています...",
    generationNoteTitle: "cogo で作成",
    generationNoteBody:
      "cogo が説明をもとにワークスペースを作成します。エディタで確認し、手直しして公開できます。",
    retry: "再試行",
    personalStyleSuffix: (labelName) => `${labelName} (個人スタイル)`,
    approvedReleaseSuffix: (labelName, releaseVersion) =>
      `${labelName} · ${releaseVersion} (承認済みリリース)`,
  },
  errors: {
    untitled: "無題",
    createFailed: "作成できませんでした",
    noProjectId: "プロジェクト ID が返りませんでした",
    unexpectedError: "予期しないエラー",
    clarificationRequired: "生成の前にソースの評価が必要です",
    notReady: "このプロジェクトはまだ生成の準備ができていません",
    preparingRequest: "依頼を準備しています",
    creatingWorkspace: "ワークスペースを作成しています",
  },
};

export const ZH_CN_NEW_PROJECT_COPY: NewProjectCopy = {
  mainArea: "创建新项目",
  sidebarTitle: "新工作",
  stepsNav: "向导步骤",
  unsavedNote: "在创建项目之前，更改不会被保存。",
  steps: {
    start: { label: "开始", sublabel: "描述目标" },
    style: { label: "样式", sublabel: "起步模板或外观" },
    shape: { label: "成形", sublabel: "名称与选择" },
    create: { label: "创建", sublabel: "打开工作区" },
  },
  footer: {
    back: "← 返回",
    cancel: "取消",
    next: "下一步 →",
    createProject: "创建项目",
    createWithCogo: "用 cogo 创建",
    creating: "创建中...",
  },
  step1: {
    kicker: "从工作开始",
    lead: "描述你想要的应用、文档或报告。然后选择最接近的起点。",
    promptLabel: "应用说明",
    promptOptional: "可选，但建议填写",
    promptPlaceholder:
      "例如：为美妆创作者做一个预约页面，包含服务介绍、用户评价、Instagram 链接，以及可直接付款的行动引导。",
    hint: "在创建项目之前，你仍可在下一步选择起步模板或视觉样式。",
    startsFromStarter: "从起步模板开始",
    startsFromDescription: "从你的描述开始",
    choose: "选择",
    paths: {
      creatorPage: {
        title: "创作者页面",
        badge: "入门",
        description: "个人品牌、链接聚合、活动、课程、粉丝页面或产品预告。",
        examplePrompt:
          "做一个个人品牌页面，包含简介、精选链接、最新动态和联系表单。",
        fallbackName: "创作者发布页",
        fallbackDescription:
          "一个公开的创作者页面，包含简介、链接、内容亮点和联系入口。",
      },
      clientApp: {
        title: "客户应用",
        badge: "面向团队",
        description: "面向真实业务的客户门户、仪表盘、受理或交接应用。",
        examplePrompt:
          "创建一个客户门户，包含仪表盘卡片、需求受理、项目状态和文件交接。",
        fallbackName: "客户门户",
        fallbackDescription: "一个面向客户的应用，包含状态、受理和交接流程。",
      },
      launchSell: {
        title: "发布并销售",
        badge: "变现",
        description:
          "为发布、收集线索、销售、预约或验证收入而设计的页面或应用。",
        examplePrompt:
          "做一个付费工作坊的发布页面，包含方案区块、结账引导、常见问题和邮件收集。",
        fallbackName: "收入发布应用",
        fallbackDescription:
          "一个以变现为中心的发布应用，包含方案、行动引导、常见问题和收集流程。",
      },
      teamWorkflow: {
        title: "团队流程",
        badge: "审批",
        description: "引导需求、评审、审批和交接的工作区。",
        examplePrompt:
          "准备一个运营工作区，包含需求受理、评审步骤、负责人备注和审批状态。",
        fallbackName: "团队流程应用",
        fallbackDescription:
          "一个团队流程应用，包含需求受理、评审步骤、负责人和审批。",
      },
    },
  },
  step2Template: {
    heading: "选择起步模板",
    lead: "为你想创建的成果，选择一个 cogo 可直接使用的起点。",
    searchPlaceholder: "搜索起步模板...",
    searchLabel: "搜索起步模板",
    all: "全部",
    loading: "正在加载起步模板…",
    emptyFiltered: (searchText) => `没有与“${searchText}”匹配的起步模板`,
    emptyNone: "暂无可用的起步模板。",
    preview: "预览",
    manifestFailed: "无法加载模板清单",
    invalidJsonResponse: "无效的 JSON 响应",
    invalidManifestFormat: "无效的清单格式",
    loadFailed: "加载失败",
    exploreMore: "看看更多",
  },
  step2Scratch: {
    heading: "外观与感觉",
    lead: "已批准的系统不可更改，并在生成与发布全过程中强制执行。个人样式只控制视觉令牌。",
    approvedSystems: "已批准的系统",
    loadingReleases: "正在加载已批准的发行版…",
    noProjectRelease: (runtimeList) =>
      `目前还没有可用于 ${runtimeList} 的、已批准的项目级系统。仅限组件的发行版不会出现在此选择器中。`,
    immutableRelease: "不可变发行版",
    personalStyles: "个人样式",
    loading: "加载中…",
    noPersonalStyle:
      "你还没有个人样式。可在下方创建一个。个人样式可以编辑，且不是本体发行版。",
    resaveLegacy: "若要用于生成，请先重新保存这个旧样式。",
    createNewStyle: "创建新样式",
    nameLabel: "名称",
    defaultStyleName: "我的样式",
    enterStyleName: "请输入样式名称。",
    descriptionLabel: "描述",
    descriptionPlaceholder: "请描述要创建的样式。",
    enterStyleDescription: "请输入样式描述。",
    saveFailed: "保存失败",
    unknownError: "未知错误",
    cancel: "取消",
    saving: "保存中…",
    saveAndContinue: "保存并继续",
    axis: {
      style: "样式",
      baseColor: "基础色",
      theme: "主题",
      chart: "图表",
      radius: "圆角",
      font: "字体",
    },
  },
  step3: {
    heading: "把工作定型",
    lead: "在 cogo 打开工作区之前，确认名称、运行位置，以及需要多少评审。",
    nameLabel: "名称",
    namePlaceholder: "例如：客户电商评审",
    nameRequiredHint: "必填 — 至少一个字符。",
    descriptionLabel: "它应该做什么",
    descriptionOptional: "（可选）",
    descriptionPlaceholder: "这个应用应该做什么？",
    workType: "工作类型",
    whereItRuns: "运行位置",
    reviewLevel: "评审级别",
    summaryStartingPoint: "起点",
    summaryStarter: "起步模板",
    summaryStyle: "样式",
    summaryWorkShape: "工作形态",
    fromStarterSuffix: (labelName) => `${labelName}（来自起步模板）`,
    fromScratch: "从零开始",
    starterFallback: "起步模板",
    appClass: {
      static: { label: "简单页面", desc: "内容与布局" },
      dynamic: { label: "可交互应用", desc: "数据与操作" },
      hybrid: { label: "带工具的页面", desc: "内容加交互" },
    },
    runtime: {
      web: { label: "网页", desc: "浏览器" },
      mobile: { label: "移动端", desc: "手机与平板" },
      flutter: { label: "Flutter", desc: "Flutter 应用" },
      rn: { label: "React Native", desc: "原生应用" },
      desktop: { label: "桌面端", desc: "桌面应用" },
    },
    security: {
      standard: { label: "标准", desc: "日常工作" },
      enterprise: { label: "团队评审", desc: "政策审查" },
      financial: { label: "敏感", desc: "涉及金钱或私密数据" },
    },
  },
  step4: {
    heading: "可以创建了",
    leadGenerate: "确认这份需求。cogo 会创建该工作并打开编辑器。",
    leadProject: "确认你的选择并创建项目。",
    summaryName: "名称",
    summaryWhatItShouldDo: "它应该做什么",
    summaryRunsOn: "运行于",
    successGenerate: "已开始创建！",
    successProject: "项目已创建！",
    openingWorkspace: "正在打开工作区...",
    openingEditor: "正在打开编辑器...",
    generationNoteTitle: "用 cogo 创建",
    generationNoteBody:
      "cogo 会根据你的描述创建工作区。你可以在编辑器中查看、打磨并发布。",
    retry: "重试",
    personalStyleSuffix: (labelName) => `${labelName}（个人样式）`,
    approvedReleaseSuffix: (labelName, releaseVersion) =>
      `${labelName} · ${releaseVersion}（已批准发行版）`,
  },
  errors: {
    untitled: "未命名",
    createFailed: "创建失败",
    noProjectId: "没有返回项目 ID",
    unexpectedError: "意外错误",
    clarificationRequired: "生成之前需要先评估来源",
    notReady: "这个项目还没有准备好生成",
    preparingRequest: "正在准备你的请求",
    creatingWorkspace: "正在创建工作区",
  },
};

export const ZH_TW_NEW_PROJECT_COPY: NewProjectCopy = {
  mainArea: "建立新專案",
  sidebarTitle: "新工作",
  stepsNav: "精靈步驟",
  unsavedNote: "在建立專案之前，變更不會被儲存。",
  steps: {
    start: { label: "開始", sublabel: "描述目標" },
    style: { label: "樣式", sublabel: "起始範本或外觀" },
    shape: { label: "成形", sublabel: "名稱與選擇" },
    create: { label: "建立", sublabel: "開啟工作區" },
  },
  footer: {
    back: "← 返回",
    cancel: "取消",
    next: "下一步 →",
    createProject: "建立專案",
    createWithCogo: "用 cogo 建立",
    creating: "建立中...",
  },
  step1: {
    kicker: "從工作開始",
    lead: "描述你想要的應用、文件或報告。接著選擇最接近的起點。",
    promptLabel: "應用說明",
    promptOptional: "選填，但建議填寫",
    promptPlaceholder:
      "例如：為美妝創作者做一個預約頁面，包含服務介紹、使用者評價、Instagram 連結，以及可直接付款的行動導引。",
    hint: "在建立專案之前，你仍可在下一步選擇起始範本或視覺樣式。",
    startsFromStarter: "從起始範本開始",
    startsFromDescription: "從你的描述開始",
    choose: "選擇",
    paths: {
      creatorPage: {
        title: "創作者頁面",
        badge: "入門",
        description: "個人品牌、連結彙整、活動、課程、粉絲頁面或產品預告。",
        examplePrompt:
          "做一個個人品牌頁面，包含簡介、精選連結、最新貼文和聯絡表單。",
        fallbackName: "創作者公開頁",
        fallbackDescription:
          "一個公開的創作者頁面，包含簡介、連結、內容亮點和聯絡入口。",
      },
      clientApp: {
        title: "客戶應用",
        badge: "團隊適用",
        description: "面向實際業務的客戶入口、儀表板、受理或交接應用。",
        examplePrompt:
          "建立一個客戶入口，包含儀表板卡片、需求受理、專案狀態和檔案交接。",
        fallbackName: "客戶入口",
        fallbackDescription: "一個面向客戶的應用，包含狀態、受理和交接流程。",
      },
      launchSell: {
        title: "發布並銷售",
        badge: "營收",
        description:
          "為發布、蒐集潛在客戶、銷售、預約或驗證營收而設計的頁面或應用。",
        examplePrompt:
          "做一個付費工作坊的發布頁面，包含方案區塊、結帳導引、常見問題和電子郵件蒐集。",
        fallbackName: "營收發布應用",
        fallbackDescription:
          "一個以營收為核心的發布應用，包含方案、行動導引、常見問題和蒐集流程。",
      },
      teamWorkflow: {
        title: "團隊流程",
        badge: "簽核",
        description: "引導需求、審查、簽核和交接的工作區。",
        examplePrompt:
          "準備一個營運工作區，包含需求受理、審查步驟、負責人備註和簽核狀態。",
        fallbackName: "團隊流程應用",
        fallbackDescription:
          "一個團隊流程應用，包含需求受理、審查步驟、負責人和簽核。",
      },
    },
  },
  step2Template: {
    heading: "選擇起始範本",
    lead: "為你想建立的成果，選一個 cogo 可直接使用的起點。",
    searchPlaceholder: "搜尋起始範本...",
    searchLabel: "搜尋起始範本",
    all: "全部",
    loading: "正在載入起始範本…",
    emptyFiltered: (searchText) => `沒有與「${searchText}」相符的起始範本`,
    emptyNone: "目前沒有可用的起始範本。",
    preview: "預覽",
    manifestFailed: "無法載入範本清單",
    invalidJsonResponse: "無效的 JSON 回應",
    invalidManifestFormat: "無效的清單格式",
    loadFailed: "載入失敗",
    exploreMore: "看看更多",
  },
  step2Scratch: {
    heading: "外觀與感受",
    lead: "已核准的系統不可更動，並在產生與發布的整個過程中強制執行。個人樣式只控制視覺代幣。",
    approvedSystems: "已核准的系統",
    loadingReleases: "正在載入已核准的發行版…",
    noProjectRelease: (runtimeList) =>
      `目前還沒有可用於 ${runtimeList} 的、已核准的專案層級系統。僅限元件的發行版不會出現在此選擇器中。`,
    immutableRelease: "不可變發行版",
    personalStyles: "個人樣式",
    loading: "載入中…",
    noPersonalStyle:
      "你還沒有個人樣式。可在下方建立一個。個人樣式可以編輯，且不是本體發行版。",
    resaveLegacy: "若要用於產生，請先重新儲存這個舊樣式。",
    createNewStyle: "建立新樣式",
    nameLabel: "名稱",
    defaultStyleName: "我的樣式",
    enterStyleName: "請輸入樣式名稱。",
    descriptionLabel: "描述",
    descriptionPlaceholder: "請描述要建立的樣式。",
    enterStyleDescription: "請輸入樣式說明。",
    saveFailed: "儲存失敗",
    unknownError: "未知的錯誤",
    cancel: "取消",
    saving: "儲存中…",
    saveAndContinue: "儲存並繼續",
    axis: {
      style: "樣式",
      baseColor: "基礎色",
      theme: "主題",
      chart: "圖表",
      radius: "圓角",
      font: "字型",
    },
  },
  step3: {
    heading: "把工作定型",
    lead: "在 cogo 開啟工作區之前，確認名稱、執行位置，以及需要多少審查。",
    nameLabel: "名稱",
    namePlaceholder: "例如：客戶電商審查",
    nameRequiredHint: "必填 — 至少一個字元。",
    descriptionLabel: "它應該做什麼",
    descriptionOptional: "（選填）",
    descriptionPlaceholder: "這個應用應該做什麼？",
    workType: "工作類型",
    whereItRuns: "執行位置",
    reviewLevel: "審查層級",
    summaryStartingPoint: "起點",
    summaryStarter: "起始範本",
    summaryStyle: "樣式",
    summaryWorkShape: "工作形態",
    fromStarterSuffix: (labelName) => `${labelName}（來自起始範本）`,
    fromScratch: "從零開始",
    starterFallback: "起始範本",
    appClass: {
      static: { label: "簡單頁面", desc: "內容與版面" },
      dynamic: { label: "可互動應用", desc: "資料與操作" },
      hybrid: { label: "附工具的頁面", desc: "內容加互動" },
    },
    runtime: {
      web: { label: "網頁", desc: "瀏覽器" },
      mobile: { label: "行動裝置", desc: "手機與平板" },
      flutter: { label: "Flutter", desc: "Flutter 應用" },
      rn: { label: "React Native", desc: "原生應用" },
      desktop: { label: "桌面", desc: "桌面應用" },
    },
    security: {
      standard: { label: "標準", desc: "日常工作" },
      enterprise: { label: "團隊審查", desc: "政策審查" },
      financial: { label: "敏感", desc: "涉及金錢或私密資料" },
    },
  },
  step4: {
    heading: "可以建立了",
    leadGenerate: "確認這份需求。cogo 會建立該工作並開啟編輯器。",
    leadProject: "確認你的選擇並建立專案。",
    summaryName: "名稱",
    summaryWhatItShouldDo: "它應該做什麼",
    summaryRunsOn: "執行於",
    successGenerate: "已開始建立！",
    successProject: "專案已建立！",
    openingWorkspace: "正在開啟工作區...",
    openingEditor: "正在開啟編輯器...",
    generationNoteTitle: "用 cogo 建立",
    generationNoteBody:
      "cogo 會依你的描述建立工作區。你可以在編輯器中檢視、打磨並發布。",
    retry: "重試",
    personalStyleSuffix: (labelName) => `${labelName}（個人樣式）`,
    approvedReleaseSuffix: (labelName, releaseVersion) =>
      `${labelName} · ${releaseVersion}（已核准發行版）`,
  },
  errors: {
    untitled: "未命名",
    createFailed: "建立失敗",
    noProjectId: "沒有回傳專案 ID",
    unexpectedError: "非預期的錯誤",
    clarificationRequired: "產生之前需要先評估來源",
    notReady: "這個專案還沒有準備好產生",
    preparingRequest: "正在準備你的請求",
    creatingWorkspace: "正在建立工作區",
  },
};
